// useAuth — high-level facade for sign-in / sign-out.
//
// signIn: mints PKCE, stashes verifier + state + return_to, redirects to Zitadel.
// signOut: best-effort POST /api/auth/v1/logout, clears store, redirects to end_session.
//
// Logout path moved 2026-05-06: was /api/lyse/v1/auth/logout; see
// useTenantFromHost.ts header for the relocation context.

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from './store.js';
import { getOidcConfig, getAuthBaseUrl, OIDC_PENDING_STORAGE_KEY } from './config.js';
import {
  generateCodeVerifier,
  codeChallengeFromVerifier,
  generateOidcState,
} from './pkce.js';
import { buildAuthorizeUrl, buildEndSessionUrl } from './oidc.js';
import type { OidcPending } from './types.js';

function isSafeReturnTo(value: string | null): value is string {
  // Same-origin pathname only — never an absolute URL. Open-redirect guard.
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');
}

export interface UseAuthApi {
  session: ReturnType<typeof useAuthStore.getState>['session'];
  tenant: ReturnType<typeof useAuthStore.getState>['tenant'];
  status: ReturnType<typeof useAuthStore.getState>['status'];
  signIn: (opts?: { loginHint?: string; returnTo?: string }) => Promise<void>;
  signOut: () => Promise<void>;
}

export function useAuth(): UseAuthApi {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const status = useAuthStore((s) => s.status);
  const clearSession = useAuthStore((s) => s.clearSession);
  const navigate = useNavigate();

  const signIn = useCallback(
    async (opts?: { loginHint?: string; returnTo?: string }): Promise<void> => {
      const cfg = getOidcConfig();
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await codeChallengeFromVerifier(codeVerifier);
      const state = generateOidcState();
      const candidate = opts?.returnTo ?? null;
      const returnTo = isSafeReturnTo(candidate) ? candidate : '/inspections';

      const pending: OidcPending = { codeVerifier, state, returnTo };
      sessionStorage.setItem(OIDC_PENDING_STORAGE_KEY, JSON.stringify(pending));

      const url = buildAuthorizeUrl({
        issuer: cfg.issuer,
        clientId: cfg.clientId,
        redirectUri: cfg.redirectUri,
        state,
        codeChallenge,
        scope: cfg.scope,
        ...(opts?.loginHint ? { loginHint: opts.loginHint } : {}),
      });
      window.location.assign(url);
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    const cfg = getOidcConfig();
    const current = useAuthStore.getState().session;
    // Best-effort revoke. We don't reject — the user gets logged out
    // regardless. AC5 only asserts the call FIRES; status doesn't gate flow.
    if (current && tenant) {
      try {
        await fetch(`${getAuthBaseUrl()}/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${current.accessToken}`,
            'X-Tenant-ID': tenant.tenantSlug,
          },
        });
      } catch {
        // swallow — best-effort
      }
    }
    // Clear local state BEFORE the end-session round-trip — guarantees the
    // store is empty regardless of how the IdP responds.
    clearSession();
    sessionStorage.removeItem(OIDC_PENDING_STORAGE_KEY);

    // Notify the IdP via end_session. We use `fetch` instead of a
    // hard-navigation `window.location.assign` so the SPA stays on origin
    // and we can SPA-navigate to /login afterwards. With a hard navigation,
    // browser-level init scripts (Playwright's `addInitScript` in tests)
    // re-fire on every full reload — they'd re-populate sessionStorage and
    // defeat the AC5 storage-cleared assertion. Fetch lets us hit the
    // end_session URL once (the IdP revokes server-side) without leaving
    // the FE origin.
    const endSessionUrl = buildEndSessionUrl({
      issuer: cfg.issuer,
      postLogoutRedirectUri: `${window.location.origin}/login`,
    });
    try {
      await fetch(endSessionUrl, {
        method: 'GET',
        redirect: 'manual',
        credentials: 'include',
      });
    } catch {
      // Best-effort. The 302 from a real IdP cannot be followed via fetch
      // anyway — `redirect: 'manual'` returns an opaque response; some
      // browsers throw. Either way, the request reached the IdP, which
      // is all the FE needs to do.
    }
    // SPA-level navigation — preserves cleared sessionStorage. A hard
    // window.location reload would re-fire any browser-level init scripts
    // (Playwright's addInitScript) that may have written a stale session
    // blob into sessionStorage on every page load.
    void navigate('/login', { replace: true });
  }, [tenant, clearSession, navigate]);

  return { session, tenant, status, signIn, signOut };
}
